import React from "react";
import TnCard from "../components/TnCard";
import KpiTile from "../components/KpiTile";
import IntakeChart, { COLOR_CLOSED, COLOR_NEW } from "./IntakeChart";
import { TnSkeleton } from "../components/TnSkeleton";
import { bucketFilterId } from "../config/cardFilters";

const KpiSkeleton = function () {
  return (
    <div className="tn-kpi-row">
      {[0, 1, 2, 3].map(function (i) {
        return (
          <div className="tn-kpi" key={i}>
            <TnSkeleton w="60%" h="12px" mb="10px" />
            <TnSkeleton w="72px" h="30px" mb="8px" />
            <TnSkeleton w="50%" h="11px" />
          </div>
        );
      })}
    </div>
  );
};

/*
Block 2 - GET dashboard/performance-overview/

Every field is read defensively: a partial payload should degrade to a dash,
not blank the card.

Each KPI tile and chart bar that comes with a `filters` entry is a
click-to-filter control for the case list (see config/cardFilters.js).
*/
const PerformanceSection = function (props) {
  const { resource, periodLabelFallback, activeFilterId, onSelectFilter } =
    props;
  const data = resource.data;
  const kpi = data && data.kpi;
  const chart = data && data.chart;
  const filters = data && data.filters;
  const periodLabel = (data && data.period_label) || periodLabelFallback;

  // Only offer the click when the response actually carries the filter.
  const tileFilter = function (key) {
    if (!onSelectFilter || !filters || !filters[key]) return {};
    const id = "performance:" + key;
    return {
      active: activeFilterId === id,
      onClick: function () {
        onSelectFilter(id);
      },
    };
  };

  // Held by reference, not rebuilt inline - see the note in IntakeChart.
  const buckets = chart && chart.buckets;
  const hasBuckets = !!(buckets && buckets.length);

  const closedPct = kpi && kpi.closed ? kpi.closed.percentage_of_intake : null;
  const netChange = kpi && kpi.open_backlog ? kpi.open_backlog.net_change : null;

  const formatNet = function (value) {
    if (value === null || value === undefined) return null;
    const prefix = value > 0 ? "+" : "";
    return prefix + value + " net this period";
  };

  return (
    <>
      <div className="tn-block-head">
        <h2 className="tn-card-title">Performance</h2>
        <span className="tn-card-subtitle">{periodLabel}</span>
      </div>
      <TnCard
        className="tn-performance"
        loading={resource.loading}
        hasLoadedOnce={resource.hasLoadedOnce}
        error={resource.error}
        onRetry={resource.reload}
        skeleton={<KpiSkeleton />}
      >
        <div className="tn-kpi-row">
          <KpiTile
            label="New tickets"
            value={kpi ? kpi.new_tickets : null}
            hint="received this period"
            {...tileFilter("new_tickets")}
          />
          <KpiTile
            label="Closed"
            value={kpi && kpi.closed ? kpi.closed.count : null}
            hint={
              closedPct === null || closedPct === undefined
                ? null
                : closedPct + "% of intake"
            }
            {...tileFilter("closed")}
          />
          <KpiTile
            label="Open backlog"
            value={kpi && kpi.open_backlog ? kpi.open_backlog.count : null}
            hint={formatNet(netChange)}
            {...tileFilter("open_backlog")}
          />
          <KpiTile
            label="Avg close time"
            value={kpi ? kpi.avg_close_time_days : null}
            suffix="d"
            hint="per resolved case"
            {...tileFilter("avg_close_time_days")}
          />
        </div>

        <div className="tn-section-divider" />

        <div className="tn-chart-head">
          <span className="tn-eyebrow">Incoming vs closed</span>
          <div className="tn-chart-legend">
            <span className="tn-legend-item">
              <span
                className="tn-legend-swatch"
                style={{ background: COLOR_NEW }}
              />
              New
            </span>
            <span className="tn-legend-item">
              <span
                className="tn-legend-swatch"
                style={{ background: COLOR_CLOSED }}
              />
              Closed
            </span>
          </div>
        </div>

        {hasBuckets ? (
          <IntakeChart
            buckets={buckets}
            onBarClick={
              onSelectFilter
                ? function (bucket, barKind) {
                    onSelectFilter(bucketFilterId(bucket, barKind));
                  }
                : null
            }
          />
        ) : (
          <div className="tn-empty tn-empty-chart">
            No activity in this period.
          </div>
        )}
      </TnCard>
    </>
  );
};

export default PerformanceSection;
